import * as React from "react";
import {
  Box,
  IconButton,
  Badge,
  useTheme,
  AppBar,
  Toolbar,
  Button,
  useMediaQuery,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  Divider,
  Container,
  Popover,
} from "@mui/material";
import { ColorModeContext, tokens } from "../../theme";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import DarkModeRoundedIcon from "@mui/icons-material/DarkModeRounded";
import LightModeRoundedIcon from "@mui/icons-material/LightModeRounded";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import MenuIcon from "@mui/icons-material/Menu";
import ProfileMenu from "./ProfileMenu";
import { useContext } from "react";
import { useAuth } from "../../Context/AuthContext";
import { useMenuItems } from "./MenuItems";
import { useNavigate, useLocation } from "react-router-dom";
import NotificationsMenu from '../Notifications/NotificationsMenu';


export default function NavBar({ title = "MUI" }) {
  // Theme and state setup
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const colorMode = useContext(ColorModeContext);
  const menuItems = useMenuItems();
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const [notificationAnchorEl, setNotificationAnchorEl] = React.useState(null);
  const [unreadCount, setUnreadCount] = React.useState(0);

  // Menu state management
  const [anchorElNav, setAnchorElNav] = React.useState(null);
  const [menuAnchors, setMenuAnchors] = React.useState({});
  
  // Check if a path is active
  const isActive = (path) => {
    return location.pathname === path;
  };

  // Handle navigation item click
  const handleNavItemClick = (path) => {
    navigate(path);
    setAnchorElNav(null);
    // Close all submenus
    setMenuAnchors({});
  };

  // Handle opening submenu
  const handleOpenSubmenu = (event, itemText) => {
    event.stopPropagation();
    setMenuAnchors(prev => ({
      ...prev,
      [itemText]: event.currentTarget
    }));
  };

  // Handle closing submenu
  const handleCloseSubmenu = (itemText) => {
    setMenuAnchors(prev => ({
      ...prev,
      [itemText]: null
    }));
  };

  const handleNotificationMenuOpen = (event) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationMenuClose = () => {
    setNotificationAnchorEl(null);
  };

  // Filter out dividers for rendering
  const filteredMenuItems = React.useMemo(() => {
    return menuItems.filter(item => !item.divider);
  }, [menuItems]);

  return (
    <AppBar
      position="sticky"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        bgcolor: "transparent",
        color: "text.primary",
        boxShadow: "none",
        borderBottom: 1,
        borderColor: "divider",
        padding: { xs: "0 10px", md: "0 26px" },
      }}
    >
      <Container maxWidth={false} disableGutters>
        <Toolbar disableGutters sx={{ display: "flex", justifyContent: "space-between" }}>
          {/* Logo/Brand - Text removed as requested */}
          <Box
            sx={{
              mr: 2,
              display: { xs: 'none', md: 'flex' }
            }}
          />

          {/* Mobile logo - Text removed as requested */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: 'flex', md: 'none' }
            }}
          />

          {/* Spacer - Menu items moved to Sidebar */}
          <Box sx={{ flexGrow: 1 }} />

          {/* Right side - Theme toggle and profile */}
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={colorMode.toggleColorMode} sx={{ ml: 1 }}>
              {theme.palette.mode === "dark" ? (
                <DarkModeRoundedIcon style={{ fontSize: 24 }} />
              ) : (
                <LightModeRoundedIcon style={{ fontSize: 24 }} />
              )}
            </IconButton>
            <Tooltip title="Notifications">
              <IconButton
                onClick={handleNotificationMenuOpen}
                aria-controls={Boolean(notificationAnchorEl) ? 'notifications-menu' : undefined}
                aria-haspopup="true"
                aria-expanded={Boolean(notificationAnchorEl) ? 'true' : undefined}
                sx={{ ml: 1 }}
              >
                <Badge badgeContent={unreadCount} color="error" overlap="circular">
                  <NotificationsNoneRoundedIcon style={{ fontSize: 24 }} />
                </Badge>
              </IconButton>
            </Tooltip>
            <NotificationsMenu
                anchorEl={notificationAnchorEl}
                open={Boolean(notificationAnchorEl)}
                onClose={handleNotificationMenuClose}
                setUnreadCount={setUnreadCount}
            />
            <ProfileMenu />
          </Box>
        </Toolbar>
      </Container>
    </AppBar>
  );
}